import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, tzHeadersSchema, sseQuerySchema } from '../schemas';

// ============================================================
// Query Schemas
// ============================================================

const dateQuerySchema = z.object({
	date: requiredStringSchema,
});

const projectGroupParamsSchema = projectIdParamsSchema.extend({
	groupId: requiredStringSchema,
});

/**
 * A CSV of ids, as a QUERY param rather than a path segment. The path segment
 * it replaces (`/todayActivitySummaries/:activityIds`) is the same shape the
 * project rule rejects for `projectIds`: a list is a filter over a collection,
 * not an address.
 */
const todayActivitySummariesQuerySchema = z.object({
	activityIds: requiredStringSchema,
});

// ============================================================
// SSE Query Schemas
// ============================================================

const mainPageStreamQuerySchema = sseQuerySchema.extend({
	projectId: requiredStringSchema,
	date: requiredStringSchema,
});

// ============================================================
// Contract
// ============================================================

export const mainPageContract = defineContract(
	'mainPage',
	{
		getMainPageDailyProfessionsActivities: route({
			method: 'GET',
			path: '/projects/:projectId/mainPageDailyProfessionsActivitiesReport',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getMainPageDailyProfessionsActivities',
			tags: ['Main Page'],
			summary: 'Get main page daily professions activities report',
		}),
		getMainPageDailyFloorsActivities: route({
			method: 'GET',
			path: '/projects/:projectId/mainPageDailyFloorsActivitiesReport',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getMainPageDailyFloorsActivities',
			tags: ['Main Page'],
			summary: 'Get main page daily floors activities report',
		}),
		getWorkplanWidgetActivities: route({
			method: 'GET',
			path: '/projects/:projectId/workplanWidgetActivitiesReport',
			params: projectIdParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getWorkplanWidgetActivities',
			tags: ['Main Page'],
			summary: 'Get workplan widget activities report',
		}),
		getWorkplanWidgetPastGroupReport: route({
			method: 'GET',
			path: '/projects/:projectId/mainPageWorkplanWidgetActivitiesGroupReport/:groupId',
			params: projectGroupParamsSchema,
			query: dateQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getWorkplanWidgetPastGroupReport',
			tags: ['Main Page'],
			summary: 'Get workplan widget past activities group report',
		}),
		getTodayActivitySummaries: route({
			method: 'GET',
			path: '/projects/:projectId/todayActivitySummaries',
			params: projectIdParamsSchema,
			query: todayActivitySummariesQuerySchema,
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getTodayActivitySummaries',
			tags: ['Main Page'],
			summary: 'Get today activity summaries by activity IDs',
		}),
	},
	{ root: true }
);

// ============================================================
// SSE Route Definitions (standalone — registered via implement())
// ============================================================

export const mainPageProfessionsStreamRoute = route({
	method: 'GET',
	path: '/mainPageDailyProfessionsActivitiesReportUpdates/stream',
	query: mainPageStreamQuerySchema,
	response: z.any(),
	operationId: 'streamMainPageDailyProfessionsActivities',
	tags: ['Main Page'],
	summary: 'Stream main page daily professions activities (SSE)',
});

export const mainPageFloorsStreamRoute = route({
	method: 'GET',
	path: '/mainPageDailyFloorsActivitiesReportUpdates/stream',
	query: mainPageStreamQuerySchema,
	response: z.any(),
	operationId: 'streamMainPageDailyFloorsActivities',
	tags: ['Main Page'],
	summary: 'Stream main page daily floors activities (SSE)',
});

export const workplanWidgetStreamRoute = route({
	method: 'GET',
	path: '/workplanWidgetActivitiesReportUpdates/stream',
	query: mainPageStreamQuerySchema,
	response: z.any(),
	operationId: 'streamWorkplanWidgetActivities',
	tags: ['Main Page'],
	summary: 'Stream workplan widget activities (SSE)',
});

export type MainPageStreamQuery = z.infer<typeof mainPageStreamQuerySchema>;
